import { CSSProperties, PropsWithChildren, useState } from "react";

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import DOMPurify from "dompurify";

import { GAP, SHOW_DELAY_MS, VIEWPORT_PADDING, Z_INDEX } from "./constants";
import { Bubble, TriggerContainer } from "./styles";
import { TooltipPosition } from "./types";
import { getSide } from "./utils";

export type TooltipProps = {
  text: string;
  /** Preferred side; the bubble flips to the opposite one when space runs out. */
  tooltipPosition: TooltipPosition;
  isDisplayed?: boolean;
  /** Maximum width in px; the bubble shrinks to fit shorter content. */
  width?: number;
  style?: CSSProperties;
};

export const Tooltip = ({
  text,
  children,
  width,
  tooltipPosition,
  style,
  isDisplayed = true,
}: PropsWithChildren<TooltipProps>) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context, placement } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: tooltipPosition,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(GAP),
      flip({ padding: VIEWPORT_PADDING }),
      shift({ padding: VIEWPORT_PADDING }),
    ],
  });

  const interactions = useInteractions([
    useHover(context, { delay: { open: SHOW_DELAY_MS, close: 0 }, move: false }),
    useDismiss(context),
    useRole(context, { role: "tooltip" }),
  ]);

  if (!isDisplayed) {
    return children;
  }

  return (
    <>
      <TriggerContainer ref={refs.setReference} style={style} {...interactions.getReferenceProps()}>
        {children}
      </TriggerContainer>
      {isOpen ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: Z_INDEX }}
            {...interactions.getFloatingProps()}
          >
            <Bubble
              side={getSide(placement)}
              maxWidth={width}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
            />
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
};
